<section class="content work-calendar-page">
  <div class="content-block work-calendar-page__content">
    <div class="block-header">
      <app-breadcrumb [title]="'MENUITEMS.ATTENDANCE.TEXT' | translate"
                      [items]="[('workCalendarCatalog.breadcrumb' | translate)]"></app-breadcrumb>
    </div>

    <div class="work-calendar-shell">
      <div class="work-calendar-shell__header">
        <span class="work-calendar-shell__icon"><mat-icon>calendar_month</mat-icon></span>
        <div class="work-calendar-shell__heading">
          <h1>{{ 'workCalendarCatalog.title' | translate }}</h1>
          <p>{{ 'workCalendarCatalog.headerDescription' | translate }}</p>
        </div>
        <div class="work-calendar-shell__hint">
          <mat-icon>info_outline</mat-icon>
          <span>{{ 'workCalendarCatalog.actions.dayActionHint' | translate }}</span>
        </div>
      </div>

      <div class="work-calendar-hero__filters" [class.work-calendar-hero__filters--single-company]="companies.length <= 1">
        @if (companies.length > 1) {
        <app-company-select class="work-calendar-toolbar-field work-calendar-toolbar-field--company"
                            [records]="companies" [value]="selectedCompanyId" [includeAll]="true"
                            [label]="'orgUnitsCatalog.fields.company' | translate"
                            [placeholder]="'common.all' | translate" [allLabel]="'common.all' | translate"
                            (valueChange)="selectedCompanyId = $event; onCompanyChanged($event)" />
        }

        <mat-form-field subscriptSizing="dynamic" appearance="outline" class="work-calendar-toolbar-field work-calendar-toolbar-field--employee">
          <mat-label>{{ 'workCalendarCatalog.filters.employee' | translate }}</mat-label>
          <mat-icon matPrefix>person_outline</mat-icon>
          <input matInput #employeeTrigger="matAutocompleteTrigger" [ngModel]="employeeSearchText"
                 (ngModelChange)="onEmployeeSearchInput($event)" [matAutocomplete]="employeeAutocomplete"
                 [placeholder]="'workCalendarCatalog.filters.employeePlaceholder' | translate" autocomplete="off"
                 (focus)="employeeTrigger.openPanel()" (click)="employeeTrigger.openPanel()" />
          <mat-icon matSuffix>expand_more</mat-icon>
          <mat-autocomplete #employeeAutocomplete="matAutocomplete" [displayWith]="displayEmployeeOption"
                            (optionSelected)="onEmployeeSelected($event.option.value)">
            @for (employee of filteredEmployees; track employee.employeeId) {
            <mat-option [value]="employee">{{ getEmployeeLabel(employee) }}</mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>

        <app-date-time-picker-field class="work-calendar-toolbar-field work-calendar-toolbar-field--date"
                                    [label]="'workCalendarCatalog.filters.dateFrom' | translate" [value]="dateFrom"
                                    mode="date" (valueChange)="dateFrom = $event; onFiltersChanged()">
        </app-date-time-picker-field>
        <app-date-time-picker-field class="work-calendar-toolbar-field work-calendar-toolbar-field--date"
                                    [label]="'workCalendarCatalog.filters.dateTo' | translate" [value]="dateTo"
                                    mode="date" (valueChange)="dateTo = $event; onFiltersChanged()">
        </app-date-time-picker-field>

        <button mat-stroked-button type="button" class="btn-tone-neutral work-calendar-hero__action-button"
                (click)="clearFilters()" [disabled]="isLoadingCalendar">
          <mat-icon>filter_alt_off</mat-icon>{{ 'workCalendarCatalog.actions.clear' | translate }}
        </button>
      </div>
    </div>

    @if (isLoadingEmployees || isLoadingCalendar) {
    <app-loading-state [overlay]="false"
                       [message]="isLoadingEmployees ? ('workCalendarCatalog.loadingEmployees' | translate) : ('workCalendarCatalog.loadingCalendar' | translate)"></app-loading-state>
    } @else if (!selectedEmployeeId) {
    <div class="work-calendar-empty">
      <mat-icon>calendar_month</mat-icon>
      <h3>{{ 'workCalendarCatalog.empty.title' | translate }}</h3>
      <p>{{ 'workCalendarCatalog.empty.description' | translate }}</p>
    </div>
    } @else if (workCalendar && currentMonth; as month) {
    <div class="work-calendar-board">
      <div class="work-calendar-board__toolbar">
        <div class="work-calendar-month__nav">
          <button mat-icon-button type="button" [disabled]="!canGoPrevMonth" (click)="goToPreviousMonth()">
            <mat-icon>chevron_left</mat-icon>
          </button>
          <div class="work-calendar-month__meta">
            <h2>{{ month.label }}</h2>
          </div>
          <button mat-icon-button type="button" [disabled]="!canGoNextMonth" (click)="goToNextMonth()">
            <mat-icon>chevron_right</mat-icon>
          </button>
          <button mat-stroked-button type="button" class="work-calendar-board__today" (click)="goToToday()">
            {{ 'workCalendarCatalog.actions.today' | translate }}
          </button>
        </div>

        <div class="work-calendar-stats">
          <div class="work-calendar-stat work-calendar-stat--work">
            <mat-icon>calendar_today</mat-icon><strong>{{ visibleWorkDays }}</strong>
            <span>{{ 'workCalendarCatalog.stats.workDays' | translate }}</span>
          </div>
          <div class="work-calendar-stat work-calendar-stat--rest">
            <mat-icon>bedtime</mat-icon><strong>{{ visibleRestDays }}</strong>
            <span>{{ 'workCalendarCatalog.stats.restDays' | translate }}</span>
          </div>
          <div class="work-calendar-stat work-calendar-stat--exception">
            <mat-icon>error_outline</mat-icon><strong>{{ visibleExceptions }}</strong>
            <span>{{ 'workCalendarCatalog.stats.exceptions' | translate }}</span>
          </div>
        </div>

        <div class="work-calendar-legend">
          <div class="work-calendar-legend__item"><i class="is-work"></i>{{ 'workCalendarCatalog.dayTypes.work' | translate }}</div>
          <div class="work-calendar-legend__item"><i class="is-rest"></i>{{ 'workCalendarCatalog.dayTypes.rest' | translate }}</div>
          <div class="work-calendar-legend__item"><i class="is-exception"></i>{{ 'workCalendarCatalog.stats.exceptions' | translate }}</div>
        </div>
      </div>

      <div class="work-calendar-grid work-calendar-grid--weekdays">
        @for (weekday of weekdayLabels; track weekday; let weekdayIndex = $index) {
        <div class="work-calendar-weekday"
             [class.is-weekend]="weekdayIndex >= 5"
             [class.is-today]="isWeekdayToday(weekdayIndex + 1)"
             [attr.aria-current]="isWeekdayToday(weekdayIndex + 1) ? 'date' : null">
          <div class="work-calendar-weekday__name">
            @if (isWeekdayToday(weekdayIndex + 1)) { <i></i> }
            <strong>{{ weekday }}</strong>
          </div>
          <span>{{ getWeekdayDateLabel(weekdayIndex + 1) }}</span>
        </div>
        }
      </div>

      <div class="work-calendar-weeks">
        @for (week of month.weeks; track $index) {
        <div class="work-calendar-grid work-calendar-grid--week">
          @for (cell of week; track trackByCell($index, cell)) {
          <div class="work-calendar-cell"
               cdkOverlayOrigin
               #dayOrigin="cdkOverlayOrigin"
               [class.work-calendar-cell--empty]="!cell.day"
               [class.work-calendar-cell--work]="cell.day && getDayTone(cell.day) === 'work'"
               [class.work-calendar-cell--rest]="cell.day && getDayTone(cell.day) === 'rest'"
               [class.work-calendar-cell--exception]="cell.day && getDayTone(cell.day) === 'exception'"
               (mouseenter)="cell.day && showDayPreview(cell.day)"
               (mouseleave)="cell.day && hideDayPreview(cell.day)">
            @if (cell.day) {
            <div class="work-calendar-cell__top">
              <strong>{{ getDayNumber(cell.day.workDate) }}</strong>
              <button mat-icon-button type="button" class="work-calendar-cell__edit-button"
                      (click)="openWorkException(cell.day); $event.stopPropagation()"
                      [attr.aria-label]="((cell.day.exception?.workExceptionId ? 'workCalendarCatalog.actions.editException' : 'workCalendarCatalog.actions.createException') | translate)">
                <mat-icon>{{ cell.day.summary.hasException ? 'edit' : 'add' }}</mat-icon>
              </button>
            </div>
            @if (getDayTone(cell.day) === 'rest') {
            <div class="work-calendar-cell__rest-banner"><mat-icon>bedtime</mat-icon>{{ 'workCalendarCatalog.dayTypes.rest' | translate }}</div>
            <h4 class="work-calendar-cell__rest-title">{{ 'workCalendarCatalog.cards.noTime' | translate }}</h4>
            } @else {
            <span class="work-calendar-cell__badge" [class.is-exception]="cell.day.summary.hasException">
              <mat-icon>{{ cell.day.summary.hasException ? 'flag' : 'light_mode' }}</mat-icon>
              {{ getDayTypeLabel(cell.day) }}
            </span>
            <h4>{{ getScheduleLabel(cell.day) }}</h4>
            <div class="work-calendar-cell__details">
              <span><mat-icon>schedule</mat-icon>{{ getTimeRangeLabel(cell.day) }}</span>
              <span><mat-icon>description</mat-icon>{{ getExpectedHoursLabel(cell.day) }}</span>
            </div>
            }

            <span class="work-calendar-cell__day-symbol"
                  [class.is-rest]="cell.day.summary.isRestDay"
                  [class.is-exception]="cell.day.summary.hasException"
                  aria-hidden="true">
              <mat-icon>{{ cell.day.summary.isRestDay ? 'dark_mode' : (cell.day.summary.hasException ? 'flag' : 'light_mode') }}</mat-icon>
            </span>

            <ng-template cdkConnectedOverlay
                         [cdkConnectedOverlayOrigin]="dayOrigin"
                         [cdkConnectedOverlayOpen]="hoveredDate === cell.day.workDate"
                         [cdkConnectedOverlayPositions]="dayPreviewPositions"
                         [cdkConnectedOverlayPush]="true">
              <article class="work-calendar-preview" role="tooltip">
                <div class="work-calendar-preview__header">
                  <div>
                    <span>{{ 'workCalendarCatalog.dayDetail.title' | translate }}</span>
                    <h3>{{ formatLongDate(cell.day.workDate) }}</h3>
                  </div>
                  <span class="work-calendar-preview__badge"
                        [class.is-rest]="getDayTone(cell.day) === 'rest'"
                        [class.is-exception]="getDayTone(cell.day) === 'exception'">
                    {{ getDayTypeLabel(cell.day) }}
                  </span>
                </div>

                <div class="work-calendar-preview__primary">
                  <div class="work-calendar-preview__schedule-icon"><mat-icon>schedule</mat-icon></div>
                  <div>
                    <span>{{ 'workCalendarCatalog.dayDetail.schedule' | translate }}</span>
                    <strong>{{ getScheduleLabel(cell.day) }}</strong>
                  </div>
                </div>

                <div class="work-calendar-preview__grid">
                  <div><span>{{ 'workCalendarCatalog.dayDetail.timeRange' | translate }}</span><strong>{{ getTimeRangeLabel(cell.day) }}</strong></div>
                  <div><span>{{ 'workCalendarCatalog.dayDetail.expectedHours' | translate }}</span><strong>{{ getExpectedHoursLabel(cell.day) }}</strong></div>
                  <div><span>{{ 'workCalendarCatalog.dayDetail.mealBreak' | translate }}</span><strong>{{ getMealBreakLabel(cell.day) }}</strong></div>
                  <div><span>{{ 'workCalendarCatalog.dayDetail.assignmentSource' | translate }}</span><strong>{{ getAssignmentSourceLabel(cell.day.assignmentSource) }}</strong></div>
                  <div><span>{{ 'workCalendarCatalog.dayDetail.cycleDayOrder' | translate }}</span><strong>{{ cell.day.cycleDayOrder ?? '-' }}</strong></div>
                  <div><span>{{ 'workCalendarCatalog.dayDetail.anchorDate' | translate }}</span><strong>{{ formatShortDate(cell.day.anchorDate) }}</strong></div>
                </div>

                <div class="work-calendar-preview__range">
                  <mat-icon>date_range</mat-icon>
                  <div>
                    <span>{{ 'workCalendarCatalog.dayDetail.effectiveRange' | translate }}</span>
                    <strong>{{ formatShortDate(cell.day.assignment?.effectiveFrom) }} — {{ formatShortDate(cell.day.assignment?.effectiveTo) }}</strong>
                  </div>
                </div>

                @if (cell.day.exception) {
                <div class="work-calendar-preview__exception">
                  <mat-icon>flag</mat-icon>
                  <span>{{ cell.day.exception.reason || ('workCalendarCatalog.dayDetail.exceptionApplied' | translate) }}</span>
                </div>
                }
              </article>
            </ng-template>

            }
          </div>
          }
        </div>
        }
      </div>
    </div>
    }
  </div>
</section>
